import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  VehicleRentApiPayload,
  VehicleRentResponse,
  VehicleRentContract,
  VehicleRentListResponse,
} from "../types";

export const vehicleRentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitVehicleRent: builder.mutation<VehicleRentResponse, VehicleRentApiPayload>({
      query: (body) => ({
        url: "/api/legal/vehicle-rentals/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "VehicleRentals" as const, id: "LIST" }],
    }),

    updateVehicleRent: builder.mutation<VehicleRentResponse, { id: number; data: VehicleRentApiPayload }>({
      query: ({ id, data }) => ({
        url: `/api/legal/vehicle-rentals/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "VehicleRentals" as const, id },
        { type: "VehicleRentals" as const, id: "LIST" },
      ],
    }),

    getVehicleRent: builder.query<VehicleRentResponse, number>({
      query: (id) => ({
        url: `/api/legal/vehicle-rentals/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "VehicleRentals" as const, id }],
    }),

    getVehicleRentList: builder.query<VehicleRentContract[], void>({
      query: () => ({
        url: `/api/legal/vehicle-rentals/`,
        method: "GET",
      }),
      transformResponse: (response: VehicleRentListResponse): VehicleRentContract[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "VehicleRentals" as const, id })),
              { type: "VehicleRentals" as const, id: "LIST" },
            ]
          : [{ type: "VehicleRentals" as const, id: "LIST" }],
    }),

    submitVehicleRentForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/legal/vehicle-rentals/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "VehicleRentals" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          vehicleRentApi.util.updateQueryData("getVehicleRent", id, (draft) => {
            draft.signing_status = 1;
          }),
        );
      },
    }),

    downloadVehicleRentPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/vehicle-rentals/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    deleteVehicleRent: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/vehicle-rentals/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "VehicleRentals" as const, id },
        { type: "VehicleRentals" as const, id: "LIST" },
      ],
    }),

    revokeVehicleRent: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/vehicle-rentals/${id}/revoke/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "VehicleRentals" as const, id },
        { type: "VehicleRentals" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
  }),
});

export const {
  useSubmitVehicleRentMutation,
  useUpdateVehicleRentMutation,
  useGetVehicleRentQuery,
  useGetVehicleRentListQuery,
  useSubmitVehicleRentForSigningMutation,
  useDownloadVehicleRentPreviewMutation,
  useDeleteVehicleRentMutation,
  useRevokeVehicleRentMutation,
} = vehicleRentApi;
